// -----------------------------------------------------------------------------
// src/pages/.../CreatorRow.jsx        – cooperates with dialog-side saving
// -----------------------------------------------------------------------------
import React, { useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { useQuery } from "react-query";
import client from "../../../../API";
import {
  getFollowersByVideos,
  getTotalFollowersInDict,
} from "../../../../Utils/constants";
import CreatorEditDialog from "./extras/CampaignCreatorEditDialog";
import CreatorChatDrawer from "./extras/CampaignCreatorChatDrawer";

/* helpers ------------------------------------------------------------------ */
const isVideoPaymentEligible = (v) =>
  !!v.liveLink && v.crossposts.every((c) => !!c.liveLink) && !v.payoutComplete;

/* component ---------------------------------------------------------------- */
export default function CreatorRow({
  creator,
  campaignDetails,
  index,
  isLaunched,
  isTcc,
  handleFieldChange,        // (creatorId, field, value, markUnsaved?)
  handleDialogOpen,         // opens file picker for brief uploads
  handleResendOfferClick,
  confirmDropCreator,
  completeCreator,
  completeVideo,
  setSelectedCreator,
  setNegotiationDialogOpen,
  saveChanges,              // still passed from parent – not used here
}) {
  /* local UI state */
  const [editOpen, setEditOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  /* notifications / unread messages */
  const notifCount = creator.notifications?.length || 0;
  const { data: unreadData } = useQuery(
    ["unreadCount", campaignDetails.id, creator.id],
    () =>
      client.conversations.getUnreadCountForCampaignCreator(
        campaignDetails.id,
        creator.id
      ),
    { enabled: isLaunched, refetchInterval: 60_000, refetchOnWindowFocus: false }
  );
  const unreadCount = unreadData?.unread_count ?? 0;

  /* videos & payment eligibility */
  const videoList = useMemo(
    () =>
      creator.videos
        .filter((v) => v.parentId == null)
        .map((v) => ({
          ...v,
          crossposts: creator.videos.filter((c) => c.parentId === v.id),
        })),
    [creator.videos]
  );
  const unpaid        = videoList.filter((v) => !v.payoutComplete);
  const eligibleVids  = unpaid.filter(isVideoPaymentEligible);
  const allEligible   = unpaid.length > 0 && unpaid.length === eligibleVids.length;

  const PaymentButtons = () => {
    if (creator.skipPayout || creator.payoutComplete) return null;

    if (allEligible) {
      return (
        <Button
          variant="contained"
          color="secondary"
          onClick={() => completeCreator({ creator: creator.id })}
          sx={{ minWidth: 160 }}
        >
          Complete Payment
        </Button>
      );
    }
    return eligibleVids.map((v) => (
      <Button
        key={v.id}
        variant="contained"
        color="secondary"
        onClick={() => completeVideo(creator.id, v.id)}
        sx={{ minWidth: 140 }}
      >
        {`Pay Video ${v.id}`}
      </Button>
    ));
  };

  /* render ------------------------------------------------------------------ */
  return (
    <>
      <Paper
        elevation={1}
        sx={{
          mb: 2,
          borderRadius: 2,
          border: `2px solid ${
            creator.status === "Accepted"
              ? "#4CAF50"
              : creator.status === "Counter"
              ? "#FFEB3B"
              : creator.status === "Approved"
              ? "#2196F3"
              : "#e0e0e0"
          }`,
        }}
      >
        <ListItem sx={{ flexWrap: "wrap", gap: 1 }}>
          {/* avatar + notif badge */}
          <ListItemAvatar>
            <Badge
              overlap="circular"
              badgeContent={notifCount}
              color="error"
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Avatar src={creator.pfphref} alt={creator.id} />
            </Badge>
          </ListItemAvatar>

          {/* name & status */}
          <ListItemText
            primary={
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="subtitle1">{creator.name}</Typography>
                <Chip
                  size="small"
                  label={
                    creator.status + (creator.payoutComplete ? " + Paid" : "")
                  }
                  sx={{
                    backgroundColor:
                      creator.status === "Accepted"
                        ? "#4CAF50"
                        : creator.status === "Counter"
                        ? "#FFEB3B"
                        : creator.status === "Approved"
                        ? "#2196F3"
                        : "#e0e0e0",
                    color:
                      creator.status === "Counter" ||
                      creator.status === "Pitched"
                        ? "rgba(0,0,0,0.87)"
                        : "white",
                  }}
                />
              </Box>
            }
            secondary={`Followers: ${
              getTotalFollowersInDict(getFollowersByVideos(creator.videos))
                ?.toLocaleString() || "N/A"
            }`}
          />

          {/* right-side buttons */}
          <Box sx={{ display: "flex", gap: 1, ml: "auto", flexWrap: "wrap" }}>
            {/* negotiate */}
            {creator.status === "Counter" && (
              <Button
                variant="contained"
                color="warning"
                onClick={() => {
                  setSelectedCreator(creator);
                  setNegotiationDialogOpen(true);
                }}
              >
                Negotiate
              </Button>
            )}

            {/* resend offer & chat (only after launch) */}
            {isLaunched && (
              <>
                {creator.status === "Pitched" && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleResendOfferClick(creator.name)}
                  >
                    Resend Offer
                  </Button>
                )}
                <Badge
                  badgeContent={unreadCount}
                  color="error"
                  invisible={unreadCount === 0}
                >
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setChatOpen(true)}
                  >
                    <ChatBubbleOutlineIcon />
                  </IconButton>
                </Badge>
              </>
            )}

            {/* payouts */}
            <PaymentButtons />

            {/* edit */}
            <IconButton size="small" onClick={() => setEditOpen(true)}>
              <EditIcon />
            </IconButton>
          </Box>
        </ListItem>
      </Paper>

      {/* -------- dialogs & drawers ----------------------------------------- */}
      <CreatorEditDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        creator={creator}
        campaignId={campaignDetails.id}
        index={index}
        handleUploadClick={handleDialogOpen}
        /* map new dialog signature -> parent */
        onFieldChange={(field, value, markUnsaved) =>
          handleFieldChange(creator.id, field, value, markUnsaved)
        }
        onDropCreator={() => confirmDropCreator(creator.id)}
        creatorId={creator.name}
        isTcc={isTcc}
      />

      <CreatorChatDrawer
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        campaignId={campaignDetails.id}
        creatorId={creator.id}
        creatorName={creator.name}
        creatorPfp={creator.pfphref}
      />
    </>
  );
}
